/**
 * Ownership — the share register, under Internal.
 *
 * Three sub-views, as the design draws them: Shareholders on the shared table,
 * Issuances as cards, and — from the 14 Aug round — Transfers as a register of
 * shares changing hands. Everything numeric is derived at read time from the
 * three ledgers, so no figure on this screen can disagree with another. See
 * `types/ownership.ts` for what is derived from what, and why nothing is stored.
 *
 * ## The two tabs answer different questions, and the wording keeps them apart
 *
 * Issuances is the company selling *new* shares; Transfers is two shareholders
 * trading *existing* ones. The arithmetic depends on nobody confusing them —
 * shares outstanding moves on the first and never on the second — so each tab
 * says which it is in a line under its own heading rather than relying on the
 * word in the tab.
 *
 * ## Admin-only, and in the rules
 *
 * The design gates the tab on a role, which is a display rule. The collections
 * are admin-only in `firestore.rules`; this screen says so rather than showing a
 * non-admin an empty register they would read as "nobody owns anything".
 */
import { useEffect, useMemo, useState } from 'react'
import { fmtKrPrice, fmtKrSum } from '@/shared/util/money'
import { useAuth } from '@/features/auth/AuthProvider'
import { ColumnPicker, DataTable } from '@/shared/components/DataTable'
import type { DataRow, FilterOptions } from '@/shared/components/DataTable'
import { KanbanBoard, type KanbanCard } from '@/shared/components/KanbanBoard'
import { ViewToggle, useViewMode } from '@/shared/components/ViewToggle'
import { useTableState } from '@/shared/hooks/useTableState'
import { formatDate, todayIso } from '@/shared/util/dates'
import { subscribeToCompany } from '@/features/settings/repository/settingsRepository'
import { EMPTY_COMPANY, type CompanyDetails } from '@/types/company'
import { haystack, matchesTokens } from '@/shared/util/tokenSearch'
import {
  agreedPriceHolders,
  effectivePrice,
  EMPTY_SHAREHOLDER,
  HOLDER_STATUSES,
  holderStatusStyle,
  holderKindLabel,
  holdings,
  interestRows,
  issuanceTotals,
  outstandingIn,
  sharesOutstanding,
  type Holding,
  type Issuance,
  type ShareholderMail,
  type ShareholderSend,
  type ShareTransfer,
  type Shareholder,
} from '@/types/ownership'
import {
  createIssuance,
  createShareholder,
  createTransfer,
  deleteIssuance,
  deleteShareholder,
  deleteTransfer,
  subscribeToOwnership,
  subscribeToShareholderSends,
  updateIssuance,
  updateShareholder,
  updateTransfer,
  replaceRegister,
  type IssuanceInput,
  type ShareholderInput,
  type TransferInput,
} from './repository/ownershipRepository'
import {
  buildCells,
  COLUMN_KEYS,
  COLUMN_META,
  ALL_COLUMNS,
  DEFAULT_COLUMNS,
  ESSENTIAL_COLUMNS,
  DESCENDING_FIRST,
  FILTERABLE_COLUMNS,
  formatPct,
  holdingOf,
  passesFilters,
  roundColumns,
  roundColumnMeta,
  buildRoundCells,
  sortShareholders,
  totalsRow,
  type ColumnKey,
  type SortKey,
  type TableKey,
} from './table'
import { ShareholderModal, emptyShareholder } from './ShareholderModal'
import { IssuanceModal, emptyIssuance } from './IssuanceModal'
import { issuanceCsv, issuanceCsvName } from './issuanceCsv'
import { TransferModal, emptyTransfer } from './TransferModal'
import { PaymentRequestModal } from './PaymentRequestModal'
import { MailLog } from './MailLog'
import { ArchivePanel } from './ArchivePanel'
import { ShareholderMailModal } from './ShareholderMailModal'
import { BoardMinutesModal } from './BoardMinutesModal'
import { BuybackMinutesModal } from './BuybackMinutesModal'
import { CapitalTimeline } from './CapitalTimeline'
import { ListMap, type MapRecord } from '@/shared/map/LazyListMap'
import { ImportCsvModal } from './ImportCsvModal'
import { planImport, toCsv, type ImportPlan } from './csv'
import { downloadCsv } from '@/shared/util/download'
import './Ownership.css'

const COLUMNS_KEY = 'skidaCrm.shareholderColumns'
const nb = (n: number) => n.toLocaleString('nb-NO')

type View = 'holders' | 'issuances' | 'transfers' | 'mail' | 'archive'

export function OwnershipWorkspace() {
  const { isAdmin, user } = useAuth()
  const [view, setView] = useState<View>('holders')
  const [holders, setHolders] = useState<Shareholder[]>([])
  const [issuances, setIssuances] = useState<Issuance[]>([])
  const [transfers, setTransfers] = useState<ShareTransfer[]>([])
  const [mail, setMail] = useState<ShareholderMail[]>([])
  /** Sends to the register. Its own subscription — see the repository. */
  const [sends, setSends] = useState<ShareholderSend[]>([])
  /** The compose dialog: open on a blank letter, or on a saved draft. */
  const [composing, setComposing] = useState<'new' | ShareholderSend | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [term, setTerm] = useState('')
  const [editingHolder, setEditingHolder] = useState<Shareholder | 'new' | null>(null)
  const [editingIssuance, setEditingIssuance] = useState<Issuance | 'new' | null>(null)
  const [editingTransfer, setEditingTransfer] = useState<ShareTransfer | 'new' | null>(null)
  /** The issuance whose subscribers are being asked to pay. */
  const [payingFor, setPayingFor] = useState<Issuance | null>(null)
  /** The increase whose board minutes are being read. */
  const [minutesFor, setMinutesFor] = useState<Issuance | null>(null)
  /** The buy-back whose own, different minutes are being read. */
  const [buybackMinutesFor, setBuybackMinutesFor] = useState<ShareTransfer | null>(null)
  const [company, setCompany] = useState<CompanyDetails>(EMPTY_COMPANY)
  /** A read file waiting for the confirmation — decision 56's second step. */
  const [importPlan, setImportPlan] = useState<ImportPlan | null>(null)

  /**
   * Two boards, and they answer different questions.
   *
   * Shareholders grouped by derived status is the investor pipeline — Potential
   * → Option holder → Shareholder → Former — which is the one reading a table
   * cannot give at a glance. Transfers has two columns, Pending and Settled,
   * because settlement is the only thing that happens to a transfer.
   */
  const [holderView, setHolderView] = useViewMode('table')
  const [transferView, setTransferView] = useViewMode('table')

  const t = useTableState<ColumnKey>({
    storageKey: COLUMNS_KEY,
    allKeys: COLUMN_KEYS,
    defaults: DEFAULT_COLUMNS,
    initialSort: 'shares',
    initialDir: 'desc',
    descendingFirst: DESCENDING_FIRST,
  })

  /**
   * Which of decision 55's two widths is showing.
   *
   * Derived from the columns rather than stored beside them, so the toggle
   * cannot disagree with the table: picking a column by hand in the wide view
   * simply means it is no longer exactly "All columns", and the button says
   * "All columns" again — which is true, and is what pressing it would do.
   */
  const allColumns =
    t.columns.length === ALL_COLUMNS.length && ALL_COLUMNS.every((c) => t.columns.includes(c))

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false)
      return
    }
    const un = subscribeToOwnership(
      (d) => {
        setHolders(d.holders)
        setIssuances(d.issuances)
        setTransfers(d.transfers)
        setMail(d.mail)
        setLoading(false)
      },
      (e) => {
        setError(e.message)
        setLoading(false)
      },
    )
    return un
  }, [isAdmin])

  // The mail log, on its own subscription: the Email tab is the only screen
  // that reads it, and the cap table should not wait for a collection it does
  // not use.
  useEffect(() => {
    if (!isAdmin) return
    return subscribeToShareholderSends(setSends, () => setSends([]))
  }, [isAdmin])

  // The company's own facts, for the two documents a round produces.
  useEffect(() => subscribeToCompany(setCompany, () => setCompany(EMPTY_COMPANY)), [])

  /**
   * Export writes the whole register, not the filtered view.
   *
   * A file named after the register that silently held only the rows matching a
   * search box would re-import as a register with everybody else deleted — and
   * the confirmation would dutifully name them, at the moment somebody was least
   * expecting to read a list.
   */
  const exportCsv = () => {
    downloadCsv(`aksjeeierbok-${todayIso()}.csv`, toCsv(holders))
  }

  const pickCsv = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setImportPlan(planImport(file.name, await file.text(), holders))
  }

  const applyImport = async () => {
    if (!importPlan || importPlan.error) return
    const keep = new Set(importPlan.rows.map((r) => r.id))
    await replaceRegister(
      // `createdAt` goes through: it is the day the holder came onto the
      // register, and `replaceRegister` writes it back so a `set` without merge
      // cannot re-date every holder to this afternoon (83). `updatedAt` does
      // not — this write is what sets it.
      importPlan.rows.map(({ updatedAt: _u, ...row }) => {
        void _u
        return row as ShareholderInput & { id: string; createdAt?: string }
      }),
      holders.filter((h) => !keep.has(h.id)).map((h) => h.id),
    )
    setImportPlan(null)
  }

  const outstanding = useMemo(() => sharesOutstanding(holders, transfers), [holders, transfers])
  /** Every holder's position, derived once — see `types/ownership.ts`. */
  const positions = useMemo(() => holdings(holders, transfers), [holders, transfers])

  const rows = useMemo(() => {
    const matched = holders
      .filter((s) => passesFilters(s, t.filters, positions))
      .filter((s) =>
        matchesTokens(term, (token) =>
          haystack([s.name, s.email, s.phone, s.linkedLabel]).includes(token),
        ),
      )
    return sortShareholders(matched, t.sortBy as SortKey, t.sortDir, positions, issuances)
  }, [holders, term, t.filters, t.sortBy, t.sortDir, positions, issuances])

  /**
   * The per-round columns belong to the wide width, not to the picker — 81.
   *
   * Derived every render from the issuance list rather than stored beside the
   * column choice, so a new round appears without anybody ticking a box and a
   * deleted one cannot survive in a saved preference. See the note on
   * `RoundColumnKey` for why they are not in `ColumnKey`.
   */
  const roundKeys = useMemo(
    () => (allColumns ? roundColumns(issuances) : []),
    [allColumns, issuances],
  )
  const tableColumns = useMemo((): TableKey[] => [...t.columns, ...roundKeys], [t.columns, roundKeys])
  const tableMeta = useMemo(
    () => ({ ...COLUMN_META, ...roundColumnMeta(issuances) }),
    [issuances],
  )

  const filterOptions = useMemo(
    (): FilterOptions<ColumnKey> => ({
      status: HOLDER_STATUSES.map((s) => ({ value: s.key, label: s.label })),
    }),
    [],
  )

  /**
   * The same filtered rows the table shows, reduced to what a map needs.
   *
   * Decision 84: one filtered set, four ways to read it. The map does not
   * filter — it is handed `rows`, which search and the column filters have
   * already been through.
   *
   * The colour is the holder's own status colour (88), so a shareholder is the
   * same colour here as on the board and in the table.
   */
  const mapRecords = useMemo(
    (): MapRecord[] =>
      rows.map((s) => {
        const pos = holdingOf(positions, s)
        const st = holderStatusStyle(pos.status)
        return {
          id: s.id,
          title: s.name,
          subtitle:
            pos.shares > 0
              ? `${nb(pos.shares)} shares · ${formatPct(pos.pct)}`
              : holderKindLabel(s),
          color: st.color,
          statusLabel: st.label,
          geo: s.geo,
          // Said in the tray, so somebody can fix the cause rather than wonder.
          missingReason: s.postalCode || s.city || s.country ? 'could not be placed' : 'no address on the record',
        }
      }),
    [rows, positions],
  )

  const dataRows = useMemo(
    (): DataRow<TableKey>[] =>
      rows.map((s) => {
        const pos = holdingOf(positions, s)
        const st = holderStatusStyle(pos.status)
        return {
          id: s.id,
          title: s.name,
          // What the row *is*, before what it holds — decision 48. A masked
          // personnummer or a public organisation number tells you which kind of
          // legal person this is without opening the record.
          subtitle: holderKindLabel(s),
          cells: [
            ...buildCells(s, t.columns, positions, issuances),
            ...buildRoundCells(s, roundKeys, issuances),
          ],
          card: {
            status: { label: st.label, bg: st.bg, color: st.color },
            lines: [
              pos.shares > 0
                ? `${nb(pos.shares)} shares · ${formatPct(pos.pct)}`
                : pos.options > 0
                  ? `${nb(pos.options)} options`
                  : 'Nothing held yet',
              s.linkedLabel,
            ],
          },
        }
      }),
    [rows, t.columns, positions, roundKeys, issuances],
  )

  /**
   * The row that closes the table — 81.
   *
   * It sums `rows`, which search and the filters have already narrowed, and
   * names `holders` so the label can say which of the two it is looking at.
   */
  const totals = useMemo(
    () => totalsRow(rows, holders, tableColumns, positions, issuances),
    [rows, holders, tableColumns, positions, issuances],
  )

  /**
   * The transfer register, and what it adds up to.
   *
   * Shares moved counts settled transfers only, for the same reason the holdings
   * do: a pending transfer has moved nothing. The last settled price is the
   * de-facto secondary valuation, which is the number this tab exists to
   * surface — it is the only price in the app that somebody outside a funding
   * round agreed to pay.
   */
  const transferSummary = useMemo(() => {
    const settled = transfers.filter((x) => x.settled)
    const priced = settled.filter((x) => x.pricePerShare !== null)
    return {
      count: transfers.length,
      moved: settled.reduce((n, x) => n + x.shares, 0),
      retired: settled.reduce((n, x) => n + (x.buyback ? x.shares : 0), 0),
      pending: transfers.length - settled.length,
      // `transfers` is newest-first, so the first priced settled row is the last
      // one that happened.
      lastPrice: priced[0]?.pricePerShare ?? null,
    }
  }, [transfers])

  /**
   * Interest recorded against an open round. Counts towards nothing else.
   *
   * `raise` is what it would come to if the round closed today, which decision
   * 76 puts on the card beside the count — and it follows `effectivePrice`, so a
   * subscriber at an agreed price is counted at the price they agreed rather
   * than at the round's.
   */
  const interestIn = (issuanceId: string) => {
    const iss = issuances.find((i) => i.id === issuanceId)
    let shares = 0
    let people = 0
    let raise = 0
    let priced = false
    for (const h of holders) {
      const rows = (h.subscriptions ?? []).filter((x) => x.issuanceId === issuanceId)
      if (rows.length === 0) continue
      people++
      for (const x of rows) {
        shares += x.shares
        const price = iss ? effectivePrice(x, iss) : null
        if (price !== null) {
          raise += x.shares * price
          priced = true
        }
      }
    }
    // Null rather than nought where nothing carries a price: a kr 0 raise reads
    // as interest worth nothing rather than as a round with no price set.
    return { shares, holders: people, raise: priced ? raise : null }
  }

  const holderName = (id: string) => holders.find((h) => h.id === id)?.name ?? 'Off the register'

  /**
   * Who is in a given round — the reason it cannot be deleted, and who the
   * payment request writes to.
   *
   * The same ledger the payment request reads: interest while the round is
   * open, purchases once it has closed. Reading purchases alone hid the Payment
   * request button on an open round whose only entries were interest, which is
   * exactly the round the request exists for.
   */
  const investorsIn = (iss: Issuance) =>
    holders
      .filter((h) =>
        (iss.status === 'ongoing' ? (h.subscriptions ?? []) : h.purchases).some(
          (p) => p.issuanceId === iss.id,
        ),
      )
      .map((h) => h.name)

  const openHolder = editingHolder === 'new' ? emptyShareholder() : editingHolder
  const openIssuance = editingIssuance === 'new' ? emptyIssuance() : editingIssuance
  const openTransfer = editingTransfer === 'new' ? emptyTransfer() : editingTransfer

  if (!isAdmin) {
    return (
      <main className="own">
        <div className="own__head">
          <div>
            <h2 className="own__title">Ownership</h2>
            <p className="own__sub">
              The share register is open to admins only. Ask one of them if you need a figure from
              it.
            </p>
          </div>
        </div>
      </main>
    )
  }

  return (
    /*
     * The issuances tab scrolls as a whole — 29 Aug 2026.
     *
     * It is not one view but four stacked blocks: the round cards, a
     * tegningsliste per round, who still owes money, and the capital timeline.
     * `.own` is a fixed-height column that hides its overflow, and only the
     * card grid carried a scrollbar — so the three blocks under it were cut off
     * at the bottom of the window with no way to reach them. Folkeinvest's 64
     * unpaid holders were on a table nobody could scroll to.
     *
     * The other two tabs keep their inner scroller, where a sticky header over
     * moving rows is worth having. This tab is a report, and a report scrolls.
     */
    <main className={`own${view === 'issuances' ? ' own--scrolls' : ''}`}>
      <div className="own__head">
        <div>
          <h2 className="own__title">Ownership</h2>
          <p className="own__sub">
            {loading
              ? 'Loading…'
              : `${holders.length} on the register · ${nb(outstanding)} shares outstanding · ` +
                `${issuances.length} round${issuances.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="own__headactions">
          {/* Export and import belong to the register, so they appear on its
              tab and nowhere else — decision 56. */}
          {view === 'holders' && (
            <>
              {/* Decision 55. A desktop affordance — the wide table is 1560px of
                  columns, which is not a phone screen — so it hides with the
                  view toggle rather than offering a width that cannot be shown. */}
              <button
                className="btn btn--outline own__widthtoggle"
                onClick={() => t.setColumns(allColumns ? ESSENTIAL_COLUMNS : ALL_COLUMNS)}
              >
                {allColumns ? 'Essential columns' : 'All columns'}
              </button>
              <button className="btn btn--outline" onClick={exportCsv} disabled={rows.length === 0}>
                Export CSV
              </button>
              <label className="btn btn--outline own__import">
                Import CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    void pickCsv(e.target.files)
                    e.target.value = ''
                  }}
                />
              </label>
            </>
          )}
        <button
          className="btn btn--primary"
          onClick={() =>
            view === 'holders'
              ? setEditingHolder('new')
              : view === 'issuances'
                ? setEditingIssuance('new')
                : setEditingTransfer('new')
          }
        >
          {view === 'holders'
            ? '+ New shareholder'
            : view === 'issuances'
              ? '+ New issuance'
              : '+ Record transfer'}
        </button>
        </div>
      </div>

      {error && <div className="empty-note">Could not read the register: {error}</div>}

      <div className="own__tabs">
        <button
          className="chip"
          aria-pressed={view === 'holders'}
          onClick={() => setView('holders')}
        >
          Shareholders
        </button>
        <button
          className="chip"
          aria-pressed={view === 'issuances'}
          onClick={() => setView('issuances')}
        >
          Issuances
        </button>
        <button
          className="chip"
          aria-pressed={view === 'transfers'}
          onClick={() => setView('transfers')}
        >
          Transfers
        </button>
        <button className="chip" aria-pressed={view === 'mail'} onClick={() => setView('mail')}>
          Email
        </button>
        <button
          className="chip"
          aria-pressed={view === 'archive'}
          onClick={() => setView('archive')}
        >
          Archive
        </button>
      </div>

      {view === 'archive' && <ArchivePanel me={user?.email ?? ''} />}

      {view === 'mail' && (
        <MailLog
          sends={sends}
          mail={mail}
          onCompose={() => setComposing('new')}
          onOpenDraft={setComposing}
        />
      )}

      {composing && (
        <ShareholderMailModal
          // Remounted per draft, so reopening a second one starts from its
          // text rather than from the first one's.
          key={composing === 'new' ? 'new' : composing.id}
          holders={holders}
          transfers={transfers}
          issuances={issuances}
          me={user?.email ?? ''}
          company={company}
          draft={composing === 'new' ? undefined : composing}
          onClose={() => setComposing(null)}
        />
      )}

      {view === 'holders' ? (
        <>
          <div className="own__filters" onClick={() => t.setOpenFilter(null)}>
            <input
              className="input own__search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Name, email, linked record…"
              aria-label="Search shareholders"
            />
            {t.filterCount > 0 && (
              <button className="chip" onClick={t.clearAllFilters}>
                Clear {t.filterCount} filter{t.filterCount === 1 ? '' : 's'}
              </button>
            )}
            <div className="colpickwrap">
              <button
                className="chip"
                onClick={(ev) => {
                  ev.stopPropagation()
                  t.setShowPicker(!t.showPicker)
                }}
                aria-expanded={t.showPicker}
              >
                Columns ({t.columns.length})
              </button>
              {t.showPicker && (
                <ColumnPicker
                  meta={COLUMN_META}
                  columns={t.columns}
                  onToggle={t.toggleColumn}
                  onClose={() => t.setShowPicker(false)}
                />
              )}
            </div>
            <ViewToggle value={holderView} onChange={setHolderView} label="Shareholders" withMap />
          </div>

          {holderView === 'map' ? (
            <ListMap
              records={mapRecords}
              onOpen={(id) => setEditingHolder(holders.find((h) => h.id === id) ?? null)}
              emptyNote="No shareholder matches these filters, so there is nothing to place."
            />
          ) : holderView === 'kanban' ? (
            <KanbanBoard
              columns={HOLDER_STATUSES.map((s) => ({ key: s.key, label: s.label, color: s.color }))}
              cards={rows.map((s) => holderCard(s, holdingOf(positions, s)))}
              onSelect={(id) => setEditingHolder(holders.find((h) => h.id === id) ?? null)}
              emptyNote={loading ? 'Loading the register…' : 'No shareholder matches these filters. Clear a filter to see the whole register.'}
            />
          ) : (
          <DataTable<TableKey>
            meta={tableMeta}
            columns={tableColumns}
            rows={dataRows}
            totals={totals}
            identityLabel="Name"
            identitySortKey="name"
            sortBy={t.sortBy}
            sortDir={t.sortDir}
            onSort={t.onSort}
            filterable={FILTERABLE_COLUMNS}
            filters={t.filters}
            filterOptions={filterOptions}
            // The filterable set is `status` alone, so the narrowing back to
            // `ColumnKey` is safe — a round column carries no filter, and
            // `filterable` is what decides which headers offer one.
            onToggleFilter={(k, v) => t.toggleFilter(k as ColumnKey, v)}
            onClearFilter={(k) => t.clearFilter(k as ColumnKey)}
            openFilter={t.openFilter}
            onOpenFilter={(k) => t.setOpenFilter(k as ColumnKey | null)}
            selectedId={editingHolder && editingHolder !== 'new' ? editingHolder.id : null}
            onSelect={(id) => setEditingHolder(holders.find((h) => h.id === id) ?? null)}
            loading={loading}
            loadingNote="Loading the register…"
            emptyNote={
              term.trim() || t.filterCount > 0
                ? 'No shareholder matches these filters.'
                : 'Nobody on the register yet. Add a shareholder once a round has been subscribed.'
            }
          />
          )}
          {allColumns && holderView === 'table' && (
            <p className="own__widthnote">
              Every column the register holds except the personnummer, which stays out of a list
              view whatever the width — it is in the record and in the export, not on screen. The
              export carries it, so a round trip through a spreadsheet is lossless.
            </p>
          )}
        </>
      ) : view === 'transfers' ? (
        <div className="own__pane">
          <p className="own__lede">
            Shares sold between existing shareholders, to someone new, or back to the company. A
            transfer never creates shares: between holders the total outstanding is unchanged, and
            a buy-back lowers it. Use Issuances when the company issues new shares.
          </p>

          <dl className="own__summary">
            <div>
              <dt>Transfers</dt>
              <dd>{transferSummary.count}</dd>
            </div>
            <div>
              <dt>Shares moved</dt>
              <dd>{transferSummary.moved > 0 ? nb(transferSummary.moved) : '—'}</dd>
              <p>
                {transferSummary.retired > 0
                  ? `settled only · ${nb(transferSummary.retired)} bought back`
                  : 'settled only'}
              </p>
            </div>
            <div>
              <dt>Pending</dt>
              <dd className={transferSummary.pending > 0 ? 'own__pending' : undefined}>
                {transferSummary.pending}
              </dd>
              <p>awaiting settlement</p>
            </div>
            <div>
              <dt>Last price</dt>
              <dd>
                {transferSummary.lastPrice === null ? '—' : fmtKrPrice(transferSummary.lastPrice)}
              </dd>
              <p>per share, secondary</p>
            </div>
          </dl>

          <div className="own__filters">
            <ViewToggle value={transferView} onChange={setTransferView} label="Transfers" />
          </div>

          {loading && <div className="empty-note">Loading…</div>}
          {!loading && transfers.length === 0 && (
            <div className="empty-note">
              No shares have changed hands yet. Record a transfer when a shareholder sells to
              another shareholder or to someone new.
            </div>
          )}
          {transfers.length > 0 &&
            (transferView === 'kanban' ? (
              <KanbanBoard
                columns={[
                  { key: 'pending', label: 'Pending', color: 'var(--color-warning-text)' },
                  { key: 'settled', label: 'Settled', color: 'var(--color-success)' },
                ]}
                cards={transfers.map((x) => transferCard(x, holderName))}
                onSelect={(id) => setEditingTransfer(transfers.find((x) => x.id === id) ?? null)}
                emptyNote="No transfer in this state. Shares move between holders on the other board."
              />
            ) : (
              <div className="own__table" role="table" aria-label="Share transfers">
                <div className="own__trhead" role="row">
                  <div role="columnheader">Date</div>
                  <div role="columnheader">Seller</div>
                  <div role="columnheader">Buyer</div>
                  <div role="columnheader">Shares</div>
                  <div role="columnheader">Price</div>
                  <div role="columnheader">Consideration</div>
                  <div role="columnheader">Status</div>
                </div>
                {transfers.map((x) => (
                  // A row rather than a button: the status pill inside it is a
                  // control of its own, and a button cannot hold a button.
                  <div
                    className="own__trrow"
                    key={x.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setEditingTransfer(x)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setEditingTransfer(x)
                      }
                    }}
                  >
                    {/* Each cell names itself on a phone, where the header row
                        is gone: without it the row read as seven unlabelled
                        values and nothing said which company was the seller. */}
                    <div data-label="Date">{x.date ? formatDate(x.date) : '—'}</div>
                    <div className="own__trname" data-label="Seller">
                      {holderName(x.fromId)}
                    </div>
                    {/* A buy-back is marked on the row it changes: from the seller's
                        side it looks like any other sale, and only this one lowers
                        what everybody else's percentage is measured against. */}
                    <div className="own__trname" data-label="Buyer">
                      {/* A5.5: one word for it, here and on the record and on
                          the board — **Buy-back**. It was "Skida AS" here,
                          "bought back by the company" on the record and
                          "Buy-back" on the kanban, which is three names for one
                          event on one screen. The clause beside it stays: it is
                          the thing that makes a buy-back different from a sale,
                          and it is not a second name. */}
                      {x.buyback ? (
                        <>
                          Buy-back <span className="own__retire">outstanding falls</span>
                        </>
                      ) : (
                        holderName(x.toId)
                      )}
                    </div>
                    <div data-label="Shares">{nb(x.shares)}</div>
                    <div data-label="Price">
                      {x.pricePerShare === null ? '—' : fmtKrPrice(x.pricePerShare)}
                    </div>
                    <div data-label="Consideration">
                      {x.pricePerShare === null ? '—' : fmtKrSum(x.shares * x.pricePerShare)}
                    </div>
                    <div data-label="Status">
                      {/* Settlement is the only field changed often enough to be
                          worth editing without opening the record — and it is the
                          one that moves shares, so it says what it will do. */}
                      <button
                        className={`pill own__state own__state--${x.settled ? 'on' : 'off'}`}
                        title={
                          x.settled
                            ? 'Settled — click to put it back to pending'
                            : 'Pending — click to settle it and move the shares'
                        }
                        onClick={(e) => {
                          e.stopPropagation()
                          void updateTransfer(x.id, { settled: !x.settled })
                        }}
                      >
                        {x.settled ? 'Settled' : 'Pending'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
        </div>
      ) : view === 'issuances' ? (
        /* Named rather than left as the fall-through it was. With three views
           an else was the issuances; with a fourth it was the issuances *and*
           anything new, which is how the Email tab came to have a list of
           funding rounds under it. */
        <div className="issuances">
          {loading && <div className="empty-note">Loading…</div>}
          {!loading && issuances.length === 0 && (
            <div className="empty-note">
              No rounds yet. A shareholder&rsquo;s shares are always bought in one, so this comes
              first.
            </div>
          )}
          {issuances.map((iss) => {
            const totals = issuanceTotals(iss, holders)
            return (
              <button
                className="issuance"
                key={iss.id}
                onClick={() => setEditingIssuance(iss)}
              >
                <div className="issuance__name">
                  {iss.name}
                  {/* An open round is the exception on this screen, so it is the
                      one that carries a pill. */}
                  {iss.status === 'ongoing' && <span className="issuance__open">Ongoing</span>}
                </div>
                <div className="issuance__meta">
                  {iss.date ? formatDate(iss.date) : 'no date'}
                  {iss.pricePerShare === null ? '' : ` · ${fmtKrPrice(iss.pricePerShare)}/share`}
                  {/* Reported to Foretaksregisteret, which is what lets this
                      round's shareholders fetch an aksjeeierbevis. Only when it
                      has been: every round is unreported until somebody records
                      the day, so the absence is the norm and not news. */}
                  {iss.registeredAt ? ` · reported ${formatDate(iss.registeredAt)}` : ''}
                </div>
                {/* Decision 78: a round with several prices says so, rather than
                    showing one price and being quietly wrong about the rest.
                    Emisjon 4 carries three — kr 663, kr 461,22 and the option
                    strike — and the line above has room for one. */}
                {agreedPriceHolders(iss, holders) > 0 && (
                  <div className="issuance__mixed">
                    {agreedPriceHolders(iss, holders)} subscriber
                    {agreedPriceHolders(iss, holders) === 1 ? '' : 's'} at agreed prices
                  </div>
                )}
                <dl className="issuance__figures">
                  <div>
                    <dt>Shares issued</dt>
                    <dd>{totals.shares > 0 ? nb(totals.shares) : '—'}</dd>
                  </div>
                  {/* Whether the money has been asked for is a fact about the
                      round, and it belongs on the round rather than in
                      somebody's sent folder. */}
                  <div>
                    <dt>Raised</dt>
                    {/* A round with no price per share has no total, and a zero
                        would read as one that raised nothing. */}
                    <dd>{totals.raised === null ? '—' : fmtKrSum(totals.raised)}</dd>
                  </div>
                  <div>
                    <dt>Investors</dt>
                    <dd>{totals.investors}</dd>
                  </div>
                </dl>
                {/* An ongoing round has interest against it and no shares. Showing
                    the interest here is the only way the card says anything true:
                    "0 issued" alone reads as a round nobody wanted. */}
                {iss.status === 'ongoing' && interestIn(iss.id).shares > 0 && (
                  <div className="issuance__interest">
                    {nb(interestIn(iss.id).shares)} shares of interest from{' '}
                    {interestIn(iss.id).holders} holder
                    {interestIn(iss.id).holders === 1 ? '' : 's'} — none issued yet
                    {/* What it would raise if it closed today — decision 76. */}
                    {interestIn(iss.id).raise !== null && (
                      <>
                        <br />
                        {fmtKrSum(interestIn(iss.id).raise!)} if it closed today
                      </>
                    )}
                  </div>
                )}
                {totals.optionShares > 0 && (
                  <div className="issuance__opt">
                    {nb(totals.optionShares)} from exercised options
                  </div>
                )}
                {/* Only that files exist — decision 19 keeps the list itself in
                    the form, and decision 54 puts the count on the card. */}
                {(iss.attachments?.length ?? 0) > 0 && (
                  <div className="issuance__att">
                    📎 {iss.attachments!.length}{' '}
                    {iss.attachments!.length === 1 ? 'attachment' : 'attachments'}
                  </div>
                )}
                {iss.paymentRequestSentAt && (
                  <div className="issuance__sent">
                    Payment request sent {formatDate(iss.paymentRequestSentAt)}
                    {iss.paymentRequestCount ? ` · ${iss.paymentRequestCount} recipients` : ''}
                  </div>
                )}
                {/* What is still owed — 28 Aug 2026. The card could say who had
                    been asked and never who had paid, which is the half that
                    makes the other half actionable. */}
                {(() => {
                  const o = outstandingIn(iss, holders)
                  if (o.rows.length === 0 && o.paidCount === 0) return null
                  return o.rows.length === 0 ? (
                    <div className="issuance__settled">All {o.paidCount} paid up</div>
                  ) : (
                    <div className="issuance__owed">
                      <strong>{fmtKrSum(o.total)}</strong> outstanding from {o.rows.length}{' '}
                      {o.rows.length === 1 ? 'holder' : 'holders'}
                      {o.paidCount > 0 && ` · ${o.paidCount} paid`}
                    </div>
                  )
                })()}
              </button>
            )
          })}
        </div>
      ) : null}

      {/*
        The tegningsliste as it stands — decision 82.

        Its own list under the cards rather than a caption on one: forty names at
        four prices is a subscription list, and a card caption cannot hold it.
        The note is the reason the list exists — "registered interest" and
        "signed subscription form, kurs kr 30,50" are a maybe and a commitment,
        and a status pill that flattened them would lose the difference. So it is
        shown as the source wrote it, verbatim and untranslated (decision 1: the
        surrounding UI is English, the recorded words are not ours to rewrite).

        Dashed throughout, because none of it has happened yet (36).
      */}
      {view === 'issuances' &&
        !loading &&
        issuances
          .filter((iss) => iss.status === 'ongoing' && interestRows(iss, holders).length > 0)
          .map((iss) => {
            const rows = interestRows(iss, holders)
            const shares = rows.reduce((n, r) => n + r.shares, 0)
            const amount = rows.reduce((n, r) => n + (r.amount ?? 0), 0)
            const anyPriced = rows.some((r) => r.amount !== null)
            return (
              <section className="tegning" key={`interest-${iss.id}`}>
                <h3 className="tegning__title">
                  Interest in {iss.name} — the tegningsliste as it stands
                </h3>
                <p className="tegning__lede">
                  Signed forms and registered interest against a round that has not closed. None of
                  these shares exists yet: nothing here counts towards a holding, a percentage or
                  the shares outstanding, and it becomes a purchase on the day the round closes and
                  the money arrives.
                </p>

                <div className="tegning__table">
                  <div className="tegning__head">
                    <div>Holder</div>
                    <div className="tegning__num">Shares</div>
                    <div className="tegning__num">Price</div>
                    <div className="tegning__num">Amount</div>
                    <div>What the source recorded</div>
                  </div>
                  {rows.map((r, i) => (
                    <div className="tegning__row" key={`${r.holderId}-${i}`}>
                      <button
                        className="tegning__name"
                        onClick={() =>
                          setEditingHolder(holders.find((h) => h.id === r.holderId) ?? null)
                        }
                      >
                        {r.holderName}
                      </button>
                      <div className="tegning__num">{nb(r.shares)}</div>
                      {/* The italic clause of 78, in the column rather than in
                          prose: a price this subscriber agreed, not the round's. */}
                      <div
                        className={`tegning__num${r.agreed ? ' tegning__num--agreed' : ''}`}
                        title={r.agreed ? 'Agreed with this subscriber, not the round’s price' : undefined}
                      >
                        {r.price === null ? '—' : fmtKrPrice(r.price)}
                      </div>
                      <div className="tegning__num">
                        {r.amount === null ? '—' : fmtKrSum(r.amount)}
                      </div>
                      {/* Verbatim. A source that says "Opsjonsavtale" and one
                          that says "Signert tegningsblankett 14.04.2026, kurs kr
                          461,22" are different commitments. */}
                      <div className="tegning__note">{r.note || '—'}</div>
                    </div>
                  ))}
                  <div className="tegning__row tegning__row--total">
                    <div>
                      Total · {nb(rows.length)} subscriber{rows.length === 1 ? '' : 's'}
                    </div>
                    <div className="tegning__num">{nb(shares)}</div>
                    {/* Blank, for the reason A4.14 gives about the shareholder
                        table's own totals row: a total of per-share prices means
                        nothing, and a figure in this cell reads as one. */}
                    <div className="tegning__num" />
                    <div className="tegning__num">{anyPriced ? fmtKrSum(amount) : '—'}</div>
                    <div className="tegning__note">If the round closed today</div>
                  </div>
                </div>
              </section>
            )
          })}

      {/*
        Who still owes money on a round — 28 Aug 2026.

        The tegningsliste above answers "who said they wanted shares"; this
        answers "who has not paid for the ones they got", which is a different
        question and the one somebody has open while making phone calls. So it
        carries the email and the phone rather than making them open each record.

        Sorted largest first: chasing is done in order of what it is worth.
        Marking a payment is on the record itself, beside the purchase it
        settles — a "paid" button on a summary line would be a click that
        silently picks which of somebody's lines it meant.
      */}
      {view === 'issuances' &&
        !loading &&
        issuances
          .map((iss) => ({ iss, owed: outstandingIn(iss, holders) }))
          .filter(({ owed }) => owed.rows.length > 0)
          .map(({ iss, owed }) => (
            <section className="owed" key={`owed-${iss.id}`}>
              <h3 className="owed__title">
                Outstanding in {iss.name} — {fmtKrSum(owed.total)} from {owed.rows.length}{' '}
                {owed.rows.length === 1 ? 'holder' : 'holders'}
              </h3>
              <p className="owed__lede">
                Shares that have been issued and not paid for. {owed.paidCount > 0 && (
                  <>
                    {owed.paidCount} {owed.paidCount === 1 ? 'holder has' : 'holders have'} settled,{' '}
                    {fmtKrSum(owed.paidTotal)} received.{' '}
                  </>
                )}
                A payment is recorded on the shareholder, on the purchase it pays for.
              </p>

              <div className="owed__table">
                <div className="owed__row owed__row--head">
                  <div>Holder</div>
                  <div className="owed__num">Shares</div>
                  <div className="owed__num">Owes</div>
                  <div>Email</div>
                  <div>Phone</div>
                </div>
                {owed.rows.map((r) => (
                  <div className="owed__row" key={r.holderId}>
                    <button
                      className="owed__name"
                      onClick={() =>
                        setEditingHolder(holders.find((h) => h.id === r.holderId) ?? null)
                      }
                    >
                      {r.holderName}
                    </button>
                    <div className="owed__num">{nb(r.shares)}</div>
                    {/* Italic where the price was agreed with them (78), so the
                        figure is never mistaken for shares × the round's price. */}
                    <div className={`owed__num${r.agreed ? ' owed__num--agreed' : ''}`}>
                      {fmtKrSum(r.amount)}
                    </div>
                    <div className="owed__contact">
                      {r.email ? (
                        <a href={`mailto:${r.email}`}>{r.email}</a>
                      ) : (
                        <span className="owed__missing">no email on record</span>
                      )}
                    </div>
                    <div className="owed__contact">
                      {r.phone ? (
                        <a href={`tel:${r.phone.replace(/\s/g, '')}`}>{r.phone}</a>
                      ) : (
                        <span className="owed__missing">no phone</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}

      {view === 'issuances' && (
        <CapitalTimeline issuances={issuances} holders={holders} transfers={transfers} />
      )}

      {openHolder && (
        <ShareholderModal
          holder={openHolder}
          isNew={editingHolder === 'new'}
          issuances={issuances}
          company={company}
          transfers={transfers}
          mail={openHolder.id ? mail.filter((m) => m.shareholderId === openHolder.id) : []}
          holderName={holderName}
          // An exercise booked into a new increase creates the issuance, so the
          // round exists in the register alongside the ones people subscribed
          // to. Priced at the grant's strike, which is what was paid for it.
          onCreateIncrease={(name, date, pricePerShare) =>
            createIssuance({ name, date, pricePerShare })
          }
          onClose={() => setEditingHolder(null)}
          onSave={async (draft: ShareholderInput, id?: string) => {
            if (id) await updateShareholder(id, draft)
            else await createShareholder(draft)
            setEditingHolder(null)
          }}
          onDelete={async (id) => {
            await deleteShareholder(id)
            setEditingHolder(null)
          }}
        />
      )}

      {openIssuance && (
        <IssuanceModal
          issuance={openIssuance}
          isNew={editingIssuance === 'new'}
          investorNames={openIssuance.id ? investorsIn(openIssuance) : []}
          onRequestPayment={() => setPayingFor(openIssuance)}
          onBoardMinutes={() => setMinutesFor(openIssuance)}
          onExportCsv={() =>
            downloadCsv(
              issuanceCsvName(openIssuance, todayIso()),
              issuanceCsv(openIssuance, holders, company.parValue),
            )
          }
          onClose={() => setEditingIssuance(null)}
          onSave={async (draft: IssuanceInput, id?: string) => {
            if (id) await updateIssuance(id, draft)
            else await createIssuance(draft)
            setEditingIssuance(null)
          }}
          onDelete={async (id) => {
            await deleteIssuance(id)
            setEditingIssuance(null)
          }}
        />
      )}

      {buybackMinutesFor && (
        <BuybackMinutesModal
          transfer={buybackMinutesFor}
          holders={holders}
          transfers={transfers}
          company={company}
          onClose={() => setBuybackMinutesFor(null)}
        />
      )}

      {importPlan && (
        <ImportCsvModal
          plan={importPlan}
          onClose={() => setImportPlan(null)}
          onApply={applyImport}
        />
      )}

      {minutesFor && (
        <BoardMinutesModal
          issuance={minutesFor}
          issuances={issuances}
          holders={holders}
          company={company}
          onClose={() => setMinutesFor(null)}
        />
      )}

      {payingFor && (
        <PaymentRequestModal
          issuance={payingFor}
          holders={holders}
          me={(user?.email ?? '').toLowerCase()}
          company={company}
          onClose={() => setPayingFor(null)}
        />
      )}

      {openTransfer && (
        <TransferModal
          transfer={openTransfer}
          isNew={editingTransfer === 'new'}
          holders={holders}
          transfers={transfers}
          outstanding={outstanding}
          onClose={() => setEditingTransfer(null)}
          onSave={async (draft: TransferInput, id?: string) => {
            if (id) await updateTransfer(id, draft)
            else await createTransfer(draft)
            setEditingTransfer(null)
          }}
          // A buyer who is not on the register yet gets a thin record: a name,
          // an email, and nothing held. The address and personnummer an official
          // filing needs are added on the record afterwards — blocking the
          // transfer on them would leave the sale unrecorded, which is worse.
          // The kind comes from the dialog now (99), so a buyer created through a
          // transfer is typed the same as one created anywhere else — and only
          // the identifier its kind actually has is written.
          onCreateBuyer={(buyer) => createShareholder({ ...EMPTY_SHAREHOLDER, ...buyer })}
          onBuybackMinutes={() => setBuybackMinutesFor(openTransfer)}
          onDelete={async (id) => {
            await deleteTransfer(id)
            setEditingTransfer(null)
          }}
        />
      )}
    </main>
  )
}

/**
 * One holder as a board card — the investor pipeline, read left to right.
 *
 * The trailing figure is what the column is about: a percentage for somebody who
 * holds shares, a grant count for somebody who only holds options, and nothing
 * for a name on the register who has neither.
 */
function holderCard(s: Shareholder, pos: Holding): KanbanCard {
  return {
    id: s.id,
    group: pos.status,
    title: s.name,
    meta: s.email || s.phone || 'no contact details',
    badge: s.linkedLabel
      ? {
          text: s.linkedLabel,
          bg: 'var(--color-surface-muted)',
          color: 'var(--color-text-muted)',
        }
      : null,
    trailing:
      pos.shares > 0
        ? formatPct(pos.pct)
        : pos.options > 0
          ? `${pos.options.toLocaleString('nb-NO')} options`
          : '',
  }
}

/** One transfer as a board card. Two columns, because settlement is the life of it. */
function transferCard(x: ShareTransfer, holderName: (id: string) => string): KanbanCard {
  return {
    id: x.id,
    group: x.settled ? 'settled' : 'pending',
    title: `${holderName(x.fromId)} → ${x.buyback ? 'Skida AS' : holderName(x.toId)}`,
    meta: x.date ? formatDate(x.date) : 'no date',
    badge: x.buyback
      ? { text: 'Buy-back', bg: 'var(--color-surface-muted)', color: 'var(--color-text-muted)' }
      : null,
    trailing: `${x.shares.toLocaleString('nb-NO')} shares`,
  }
}
